import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Percent, Coins, FileText, SlidersHorizontal, Settings2, Tag, Layers, ChevronRight } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { PageHeader } from '@/components/ui/page-header';
import { EmptyState } from '@/components/ui/empty-state';
import { useAuthStore } from '@/stores/auth.store';
import { matchesPermission } from '@/config/navigation';

interface MasterDataItem {
  to: string;
  label: string;
  description: string;
  icon: typeof Percent;
  permission?: string;
}

const ITEMS: MasterDataItem[] = [
  { to: '/catalog/categories', label: 'mdCategories', description: 'mdCategoriesDesc', icon: Tag, permission: 'products:read' },
  { to: '/catalog/brands', label: 'mdBrands', description: 'mdBrandsDesc', icon: Tag, permission: 'products:read' },
  { to: '/catalog/price-tiers', label: 'mdPriceTiers', description: 'mdPriceTiersDesc', icon: Layers, permission: 'price-tiers:read' },
  { to: '/admin/master-data/tax-rates', label: 'mdTaxRates', description: 'mdTaxRatesDesc', icon: Percent, permission: 'tax-rates:read' },
  { to: '/admin/master-data/currencies', label: 'mdCurrencies', description: 'mdCurrenciesDesc', icon: Coins, permission: 'multi-currency:read' },
  { to: '/admin/master-data/templates', label: 'mdTemplates', description: 'mdTemplatesDesc', icon: FileText, permission: 'product-templates:read' },
  { to: '/admin/master-data/menu-modifiers', label: 'mdModifiers', description: 'mdModifiersDesc', icon: SlidersHorizontal, permission: 'restaurant:write' },
  { to: '/admin/master-data/restaurant-setup', label: 'mdRestaurant', description: 'mdRestaurantDesc', icon: Settings2, permission: 'restaurant:write' },
];

/**
 * Administration → Master Data hub. Reference/configuration data that is
 * consumed inside workflows but never warrants a top-level nav item.
 */
export function MasterDataHubPage() {
  const { t } = useTranslation('nav');
  const hasPermission = useAuthStore((s) => s.hasPermission);
  const items = ITEMS.filter((i) => matchesPermission(i.permission, hasPermission));

  return (
    <div className="space-y-6">
      <PageHeader title={t('masterData')} subtitle={t('mdSubtitle')} />

      {items.length === 0 ? (
        <EmptyState icon={Settings2} title={t('mdEmptyTitle')} description={t('mdEmptyDesc')} />
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {items.map((item) => {
            const Icon = item.icon;
            return (
              <Link key={item.to} to={item.to}>
                <Card className="group h-full transition-all duration-200 hover:border-primary/30 hover:shadow-md dark:hover:border-primary/30">
                  <CardContent className="flex items-start gap-3 p-4">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary-text">
                      <Icon className="h-5 w-5" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="flex items-center gap-1 font-semibold">
                        {t(item.label)}
                        <ChevronRight className="h-4 w-4 opacity-0 transition-opacity group-hover:opacity-60" />
                      </p>
                      <p className="text-xs text-muted-foreground">{t(item.description)}</p>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
